// 안드로이드 시스템/하드웨어 뒤로가기 동작 제어.
//  - 메인('/'): 두 번 연속 누르면 종료(첫 번째는 안내 토스트).
//  - 탭 화면(카테고리/장바구니/마이페이지): 메인으로.
//  - 그 외(상세/적립금/쿠폰 등): 이전 화면으로.
//  네이티브(Capacitor)는 backButton 이벤트로 정확히 처리하고, 웹 PWA 는 popstate 가드로 메인 종료를 best-effort 처리한다.
//  (웹에서 탭->메인 은 BottomTabBar 의 얕은 히스토리 스택으로, 상세->이전 은 브라우저 기본 동작으로 충족.)

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isNativeApp } from "@/platform/environment.ts";
import { exitNativeApp, registerHardwareBack } from "@/platform/backButton.ts";

const TAB_ROUTES = new Set(["/category", "/cart", "/me"]);
const EXIT_WINDOW_MS = 2000;

// 시스템 뒤로가기 동작을 라우트 종류별로 제어하고, 종료 안내 토스트를 렌더한다.
export default function BackButtonController() {
  const navigate = useNavigate();
  const location = useLocation();
  const armedAtRef = useRef(0);
  const [exitHint, setExitHint] = useState(false);

  // 종료 안내 토스트 자동 사라짐.
  useEffect(() => {
    if (!exitHint) {
      return;
    }
    const t = setTimeout(() => setExitHint(false), EXIT_WINDOW_MS);
    return () => clearTimeout(t);
  }, [exitHint]);

  // 네이티브: backButton 이벤트로 전 경로 규칙 처리.
  useEffect(() => {
    if (!isNativeApp()) {
      return;
    }
    return registerHardwareBack(() => {
      const path = window.location.pathname;
      if (path === "/") {
        if (Date.now() - armedAtRef.current < EXIT_WINDOW_MS) {
          exitNativeApp();
        } else {
          armedAtRef.current = Date.now();
          setExitHint(true);
        }
        return;
      }
      if (TAB_ROUTES.has(path)) {
        navigate("/");
        return;
      }
      navigate(-1);
    });
  }, [navigate]);

  // 웹 PWA: 메인에서만 popstate 가드로 두 번 눌러 종료.
  useEffect(() => {
    if (isNativeApp() || location.pathname !== "/") {
      return;
    }
    // 메인에 가드 엔트리를 얹어, 첫 뒤로가기가 앱을 즉시 벗어나지 않게 한다.
    window.history.pushState(null, "");
    const onPop = () => {
      if (Date.now() - armedAtRef.current < EXIT_WINDOW_MS) {
        armedAtRef.current = 0;
        window.history.back(); // 실제 종료(가드 없이 한 번 더 뒤로)
        return;
      }
      armedAtRef.current = Date.now();
      setExitHint(true);
      window.history.pushState(null, ""); // 다시 가드
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [location.pathname]);

  if (!exitHint) {
    return null;
  }
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(6rem+var(--safe-bottom))] z-50 flex justify-center">
      <div className="rounded-full bg-black/80 px-4 py-2 text-sm font-medium text-white shadow">
        뒤로 한 번 더 누르면 종료됩니다
      </div>
    </div>
  );
}
